import React, { useMemo } from "react";
import moment from "moment";
import styled from "styled-components";
import { UI_CAPTION, colors2 } from "oolib";

/**
 * The read-only-plus-a-click "range brush": a slim track spanning the whole
 * corpus range, a highlighted window over the selected period that ANIMATES
 * to its new position on change, and month columns that jump the period on
 * click (same resolver + toasts as the sparkline segment clicks — a month
 * with no data explains itself instead of dead-clicking).
 *
 * Custom rather than recharts' <Brush>: tried both, the brush's embedded
 * chart chrome was too much for a period indicator strip.
 *
 * All period options are month-aligned, so everything runs in month units
 * over equal-width columns: window edges always land on column boundaries.
 */

const Wrap = styled.div`
  position: relative;
  margin: 2px 0 24px;
`;

// Fixed-height stage for the axis: track and window both center on 50% of
// it, so they share one axis by construction (a margin on the track itself
// would collapse out of the wrapper and float the bar off-center).
const Strip = styled.div`
  position: relative;
  height: 18px;
`;

const Track = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  height: 4px;
  border-radius: 2px;
  background: ${colors2.grey10};
`;

// The selected period: an outlined pill AROUND the track rather than a
// filled block over it — the track stays visible through it.
const Window = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 16px;
  border-radius: 9px;
  border: 1.5px solid ${colors2.grey40};
  background: transparent;
  box-sizing: border-box;
  transition: left 0.35s cubic-bezier(0.2, 0.6, 0.2, 1),
    width 0.35s cubic-bezier(0.2, 0.6, 0.2, 1);
  pointer-events: none;
`;

const LabelsRow = styled.div`
  display: flex;
`;

const LabelCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
`;

// Full-height invisible click columns over track + labels — a generous hit
// target per month, with a soft column tint on hover as the affordance.
const ColumnsOverlay = styled.div`
  position: absolute;
  inset: -2px 0;
  display: flex;
`;

const Column = styled.div`
  flex: 1 1 0;
  min-width: 0;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: ${colors2.grey5};
  }
`;

export const PeriodTimeline = ({ globalRange, period, onMonthClick }) => {
  const model = useMemo(() => {
    if (!globalRange?.min || !globalRange?.max) return null;
    const start = moment(globalRange.min).startOf("month");
    const end = moment(globalRange.max).startOf("month");
    const monthCount = end.diff(start, "months") + 1;
    if (monthCount < 1 || monthCount > 240) return null;

    const months = Array.from({ length: monthCount }, (_, i) =>
      start.clone().add(i, "months")
    );
    const step = Math.max(1, Math.ceil(monthCount / 10));

    let window = null;
    let inWindow = () => false;
    if (period?.value === "all_time") {
      window = { left: 0, width: 100 };
      inWindow = () => true;
    } else if (period?.from && period?.to) {
      const rawFrom = moment(period.from).startOf("month").diff(start, "months");
      // Partial months round UP ("this year" runs Jan..now and must include
      // the current month).
      const rawSpan = Math.max(
        1,
        Math.ceil(moment(period.to).diff(moment(period.from), "months", true))
      );
      // Intersect the period with the corpus range — a year option can start
      // before the first data month (year_2025 when the data starts Aug '25),
      // and clamping only the left edge would smear the window across the
      // whole strip.
      const fromIdx = Math.max(0, rawFrom);
      const endIdx = Math.min(rawFrom + rawSpan, monthCount);
      if (endIdx > fromIdx) {
        window = {
          left: (fromIdx / monthCount) * 100,
          width: ((endIdx - fromIdx) / monthCount) * 100,
        };
        inWindow = (i) => i >= fromIdx && i < endIdx;
      }
    }
    return { months, step, monthCount, window, inWindow };
  }, [globalRange, period]);

  if (!model) return null;

  return (
    <Wrap>
      <Strip>
        <Track />
        {model.window && (
          <Window
            style={{ left: `${model.window.left}%`, width: `${model.window.width}%` }}
          />
        )}
      </Strip>
      <LabelsRow>
        {model.months.map((m, i) => (
          <LabelCell key={m.format("YYYY-MM")}>
            {(i % model.step === 0 || i === model.monthCount - 1) && (
              <UI_CAPTION
                style={{
                  // Labels inside the selected window read darker — the text
                  // echoes what the window is highlighting.
                  color: model.inWindow(i) ? colors2.grey80 : colors2.grey50,
                  transition: "color 0.35s ease",
                }}
              >
                {m.format("MMM 'YY")}
              </UI_CAPTION>
            )}
          </LabelCell>
        ))}
      </LabelsRow>
      {onMonthClick && (
        <ColumnsOverlay>
          {model.months.map((m) => (
            <Column
              key={m.format("YYYY-MM")}
              title={m.format("MMMM YYYY")}
              onClick={() => onMonthClick(m.format("YYYY-MM"))}
            />
          ))}
        </ColumnsOverlay>
      )}
    </Wrap>
  );
};
